import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { FaPhoneAlt } from "react-icons/fa";
import { FaAddressCard } from "react-icons/fa6";
import emailjs from "@emailjs/browser";

const servicesOptions = [
  "Uniformed Security",
  "Mobile Security",
  "Fire Watch Security",
  "Concierge Security",
  "Elite Suite & Tie Security",
  "Executive Protection",
  "Gatehouse Security",
  "Parking Solutions",
  "Event Security",
  "Camera Installations",
  "Camera Monitoring",
  "Loss Prevention",
  "Security Systems",
  "Consulting Services",
];

const Contact = () => {
  const [activeTab, setActiveTab] = useState("service");
  const formSectionRef = useRef(null);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setTimeout(() => {
      formSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  return (
    <div className="w-full min-h-screen text-gray-800">
      <section className="flex items-center justify-center flex-col bg-red-800 text-white py-16 px-6 md:px-20">
        <h1 className="text-4xl md:text-5xl font-bold">Contact Us</h1>
        <p className="mt-3 text-lg md:text-xl max-w-2xl">
          How can <span className="font-semibold">New Way Security</span> assist
          you today?
        </p>
        <div className="flex flex-wrap gap-4 mt-6">
          <button
            onClick={() => handleTabChange("service")}
            className={`px-6 py-2 rounded-full text-sm md:text-base transition font-semibold ${
              activeTab === "service"
                ? "bg-white text-red-800"
                : "bg-transparent border border-white hover:bg-white hover:text-red-800"
            }`}
          >
            Service Inquiry / Get a Quote
          </button>
          <button
            onClick={() => handleTabChange("general")}
            className={`px-6 py-2 rounded-full text-sm md:text-base transition font-semibold ${
              activeTab === "general"
                ? "bg-white text-red-800"
                : "bg-transparent border border-white hover:bg-white hover:text-red-800"
            }`}
          >
            General Inquiry
          </button>
        </div>
      </section>

      <section className="py-12 px-6 md:px-20" ref={formSectionRef}>
        <div className="flex items-center justify-center mb-6">
          <h2 className="text-4xl font-bold text-red-800">Our Offices</h2>
        </div>
        <div className="grid place-items-center md:grid-cols-1 gap-6 mb-6">
          <div className="bg-white rounded shadow-lg w-[60%] border border-red-800 p-4 flex justify-center flex-col items-center">
            <FaAddressCard size={50} />
            <h3 className="text-3xl font-semibold text-red-800 mb-1">
              Head Office
            </h3>
            <p className="text-gray-700 text-center text-lg leading-relaxed">
              <span className="text-black">Email:</span> info@newwaysecurity.com
            </p>
            <p className="text-gray-700 text-center text-lg leading-relaxed">
              <span className="text-black">Contact Numbers:</span> +1 (437) 985
              0552, +1 (437) 286 3002
            </p>
            <p className="text-gray-700 text-center text-lg leading-relaxed">
              600 Matheson Blvd W, Unit 5 Inside Venture X, <br /> Mississauga,
              ON, L5R 4B8.
            </p>
          </div>
        </div>

        <div className="flex justify-end text-red-800 items-center gap-2 mb-4">
          <FaPhoneAlt />
          <span className="text-sm md:text-base">+1 (437) 985 0552</span>
        </div>
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white p-8 rounded-2xl shadow-lg"
        >
          <h2 className="text-2xl font-bold text-red-800 mb-2">
            {activeTab === "service"
              ? "Service Inquiry / Get a Quote"
              : "General Inquiry"}
          </h2>
          <p className="text-gray-600 mb-6">
            {activeTab === "service"
              ? "Please complete the form below and a member of our Sales Team will be in touch shortly."
              : "Tell us what you need and a representative from New Way Security will respond promptly."}
          </p>
          <FormSection formType={activeTab} />
        </motion.div>
      </section>
    </div>
  );
};

const FormSection = ({ formType }) => {
  const isService = formType === "service";
  const formRef = useRef();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const sendEmail = (e) => {
    e.preventDefault();

    const form = formRef.current;

    emailjs.sendForm("", "", form, "").then(
      () => {
        emailjs.sendForm("", "", form, "");
        setIsSubmitted(true);
        form.reset();
      },
      (error) => {
        console.error("EmailJS Error:", error.text);
      }
    );
  };

  const Input = ({ name, placeholder, required = false, type = "text" }) => (
    <div className="group relative w-full">
      <input
        type={type}
        name={name}
        required={required}
        placeholder=" "
        className="peer w-full rounded-xl border border-gray-300 bg-white px-4 pt-5 pb-2 text-sm text-gray-900 placeholder-transparent shadow-sm transition-all duration-300 focus:border-red-800 focus:outline-none focus:ring-2 focus:ring-red-800"
      />
      <label className="absolute left-4 top-2.5 text-xs text-gray-500 transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:top-2.5 peer-focus:text-xs peer-focus:text-red-800">
        {placeholder}
      </label>
    </div>
  );

  return isSubmitted ? (
    <div className="text-green-600 font-semibold text-lg">
      ✅ Thank you! Your inquiry has been submitted.
    </div>
  ) : (
    <form
      ref={formRef}
      onSubmit={sendEmail}
      className="grid grid-cols-1 md:grid-cols-2 gap-6"
    >
      <Input name="first_name" placeholder="First Name *" required />
      <Input name="last_name" placeholder="Last Name" />

      {isService && (
        <>
          <Input name="company" placeholder="Company Name" />
          <Input name="title" placeholder="Designation" />
        </>
      )}

      <Input name="email" placeholder="Email *" type="email" required />
      <Input name="phone" placeholder="Phone Number *" required />

      {isService && (
        <>
          <Input name="office_phone" placeholder="Office Phone" />
          <Input name="city" placeholder="City" />
          <Input name="province" placeholder="Province" />
          <Input name="industry" placeholder="Industry" />
          <div className="group relative w-full">
            <select
              name="service_required"
              required
              className="peer w-full rounded-xl border border-gray-300 bg-white px-4 pt-5 pb-2 text-sm text-gray-900 shadow-sm transition-all duration-300 focus:border-red-800 focus:outline-none focus:ring-2 focus:ring-red-800"
              defaultValue=""
            >
              <option value="" disabled>
                Select a Service *
              </option>
              {servicesOptions.map((service, idx) => (
                <option key={idx} value={service}>
                  {service}
                </option>
              ))}
            </select>
            <label className="absolute left-4 top-1 text-xs text-gray-500 transition-all peer-focus:text-red-800">
              Service Required
            </label>
          </div>

          <Input name="referral" placeholder="How did you hear about us?" />
        </>
      )}

      <div className="md:col-span-2">
        <div className="group relative">
          <textarea
            name="message"
            placeholder=" "
            required
            rows="4"
            className="peer w-full rounded-xl border border-gray-300 bg-white px-4 pt-5 pb-2 text-sm text-gray-900 placeholder-transparent shadow-sm transition-all duration-300 focus:border-red-800 focus:outline-none focus:ring-2 focus:ring-red-800"
          ></textarea>
          <label className="absolute left-4 top-2.5 text-xs text-gray-500 transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:top-2.5 peer-focus:text-xs peer-focus:text-red-800">
            {isService ? "Additional Comments" : "Reason for Inquiry *"}
          </label>
        </div>
      </div>

      <div className="md:col-span-2">
        <button
          type="submit"
          className="w-full md:w-auto px-6 py-3 bg-red-800 text-white font-semibold rounded-xl hover:bg-red-700 transition"
        >
          Submit
        </button>
      </div>
    </form>
  );
};

export default Contact;
